import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import ReactApexChart from 'react-apexcharts'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'

/**
 * ANALITIKA BOSH EKRANI — 2026-08-21 da qayta qurildi.
 *
 * SAVOL O'ZGARDI. Ilgari ekran «qancha foyda qildim?» degan savolga javob
 * berardi va uni FOYDA orqali berardi — foyda esa tannarxga tayanadi,
 * tannarx bir necha manbadan keladi va ishonchsiz.
 *
 * Yangi savol: «PULIM QAYERDA VA HARAKATLANYAPTIMI?». Lokal bazada
 * o'lchangan sabab:
 *   · qarz 852 mln = 96 KUNLIK savdo, uning 585 mln i (69%) 120 kundan eski;
 *   · omborda tayyor holda 178.7 mln so'm o'rtacha 26 kun yotibdi;
 *   · tonnadagi marja $223 (aprel) dan $173 ga tushgan.
 *
 * ⚠️ Foyda YO'Q QILINMADI — «Ялпи даромад» ikkinchi qatorda qoldi.
 *
 * ═══ VIZUAL QOIDALAR (2026-08-21 auditi natijasi) ═══
 *  · KPI kartalari BITTA komponentdan (`KpiCard`) chiqadi — ikki xil karta
 *    uslubi ekranni «buzilgan» ko'rsatgan edi.
 *  · Grafikda ikkita o'lcham hech qachon bir vaqtda ko'rsatilmaydi.
 *  · Har qattiq rang uchun qorong'i mavzuda o'zgaruvchi bo'lishi shart.
 */

const QUICK_FILTERS = [
	{ id: 'today', label: 'Bugun' },
	{ id: 'yesterday', label: 'Kecha' },
	{ id: 'week', label: '7 kun' },
	{ id: 'month', label: '30 kun' },
]

/* Grafik o'lchovlari — «dinamika yoqmayabdi» ning sababi ikki o'qli
   (savdo + chek soni) chiziq edi: ikki xil birlik bitta rasmda. Endi
   bir vaqtda BITTA o'lcham, tab bilan almashadi. */
const CHART_TABS = [
	{ id: 'sales', label: 'Савдо', color: '#0d6efd', money: true },
	{ id: 'collected', label: 'Йиғилган пул', color: '#20c997', money: true },
	{ id: 'tonna', label: 'Тонна', color: '#fd7e14', money: false },
]

/* Qarz yoshi. Yashildan qizilgacha — eskirgani sayin xavf ortadi. */
const AGING_COLORS = ['#198754', '#a3c14a', '#ffc107', '#fd7e14', '#dc3545']

function getDateRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'today') return { start: fmt(now), end: fmt(now) }
	if (id === 'yesterday') {
		const d = new Date(now); d.setDate(d.getDate() - 1)
		return { start: fmt(d), end: fmt(d) }
	}
	if (id === 'week') {
		const d = new Date(now); d.setDate(d.getDate() - 6)
		return { start: fmt(d), end: fmt(now) }
	}
	if (id === 'month') {
		const d = new Date(now); d.setDate(d.getDate() - 29)
		return { start: fmt(d), end: fmt(now) }
	}
	return { start: fmt(now), end: fmt(now) }
}

/**
 * PUL — so'mda kasr ma'nosiz.
 *
 * ⚠️ `formatMoney(x)` kelgan qiymatda nuqta bo'lsa kasrni SAQLAYDI
 *    (helpers.js:127 — `hasDecimal`), shuning uchun qarz «852 435 769.36»
 *    bo'lib chiqardi. Backendда ikki maydon float keladi: `debt.total`
 *    (clients_balances.balance) va `gross_income`.
 * ⚠️ `formatMoney(x, 0)` YECHIM EMAS: u kasrni kesadi-yu, NUQTANI QOLDIRADI
 *    («852 435 769.») — chunki `hasDecimal` baribir rost. Avval yaxlitlanadi.
 * ⚠️ Ma'lumot kelmaguncha «0» emas, «—» — soxta nol yonib o'chmasin.
 */
const som = v => (v === null || v === undefined ? '—' : formatMoney(Math.round(Number(v) || 0)))

/** O'sish chipi. `invert` — qarz/qaytarish uchun: o'sishi YOMON. */
function Delta({ value, invert }) {
	// null = oldingi davr nol edi; «+100%» deb yozish yolg'on bo'lardi
	if (value === null || value === undefined) {
		return <span className="an-chip" style={{ color: 'var(--dk-text-2, #adb5bd)', background: 'var(--an-chip-bg, #f1f3f7)' }}>—</span>
	}
	const up = value > 0
	const good = invert ? !up : up
	const color = value === 0
		? 'var(--dk-text-2, #6c757d)'
		: (good ? 'var(--ok-mid, #198754)' : 'var(--warn-ink, #dc3545)')
	/* Fon NEYTRAL (rangning shaffof varianti emas): rang CSS o'zgaruvchisidan
	   keladi, unga `+ '1f'` qo'shib bo'lmaydi — `var(--x, #dc3545)1f` yaroqsiz
	   CSS bo'lib, fon umuman chizilmasdi. */
	return (
		<span className="an-chip" style={{ color, background: 'var(--an-chip-bg, #f1f3f7)' }}>
			<i className={up ? 'uil-arrow-up' : (value < 0 ? 'uil-arrow-down' : 'uil-minus')} />
			{value > 0 ? '+' : ''}{value}%
		</span>
	)
}

/**
 * KPI kartasi — ikkala qator ham SHU komponentdan chiqadi.
 * Ilgari «katta» va «kichik» ikki xil karta yozilgan edi va ular
 * bir-biriga o'xshamasdi — ekran shundan buzilgan ko'rinardi.
 */
function KpiCard({ label, icon, color, bg, value, delta, invert, sub, right, big = true }) {
	return (
		<div className="card an-kpi h-100" style={{ '--kpi-accent': color }}>
			<div className="card-body py-3 px-3 d-flex flex-column">
				<div className="d-flex align-items-center gap-2">
					{/* ⚠️ Ikonka foni ALOHIDA prop (`bg`), `color + '1f'` EMAS:
					    ranglarning yarmi CSS o'zgaruvchisi (`var(--warn-ink, …)`),
					    unga satr qo'shilsa yaroqsiz CSS chiqadi va fon yo'qoladi. */}
					{icon &&
						<div className="an-kpi-ico" style={{ background: bg }}>
							<i className={icon} style={{ color }} />
						</div>}
					<span className="an-kpi-lbl">{label}</span>
					<span className="ms-auto">
						{right !== undefined ? right : <Delta value={delta ?? null} invert={invert} />}
					</span>
				</div>
				<div className="an-kpi-val" style={{ color, fontSize: big ? 25 : 19 }}>{value}</div>
				{/* mt-auto — sub matn pastga yopishadi, kartalar teng ko'rinadi */}
				{sub && <div className="text-muted an-kpi-sub mt-auto">{sub}</div>}
			</div>
		</div>
	)
}

export default function AnalyticsDashboard() {
	const now = new Date()
	const [activeFilter, setActiveFilter] = useState('month')
	const [startDate, setStartDate] = useState(new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() + 1))
	const [endDate, setEndDate] = useState(now)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(false)

	const [chartTab, setChartTab] = useState('sales')
	/* Yig'indi (narastayushiy) — sukut bo'yicha YOQIQ: oyiga ~25 buyurtma,
	   bitta chek 68 mln — kunlik grafik tekis chiziq + 3 cho'qqi bo'ladi. */
	const [cumulative, setCumulative] = useState(true)

	const load = useCallback(async (sd, ed) => {
		setLoading(true)
		const res = await GET('/analytics/dashboard', {
			start_date: formatDate(sd, 'yyyy-MM-dd'),
			end_date: formatDate(ed, 'yyyy-MM-dd'),
		}, { loader: false })
		setLoading(false)
		if (httpOk(res)) setData(res.data)
	}, [])

	useEffect(() => {
		const range = getDateRange('month')
		load(new Date(range.start), new Date(range.end))
	}, [load])

	function applyQuick(id) {
		setActiveFilter(id)
		const range = getDateRange(id)
		const s = new Date(range.start), e = new Date(range.end)
		setStartDate(s); setEndDate(e)
		load(s, e)
	}

	const kpi = data?.kpi || {}
	const delta = data?.delta || {}
	const debt = data?.debt || { aging: [] }
	const stuck = data?.stuck || {}
	// useMemo — har renderда yangi obyekt yaratilsa quyidagi seriyalar
	// har safar qayta hisoblanardi (grafik bejiz miltillardi)
	const chart = useMemo(() => data?.chart || { labels: [] }, [data])
	const top = data?.top_products || {}

	const tab = CHART_TABS.find(x => x.id === chartTab) || CHART_TABS[0]
	// Marja kartasi — rulonsiz tenantda (tonna umuman yo'q) ko'rsatilmaydi
	const hasMargin = kpi.margin_usd_t !== null && kpi.margin_usd_t !== undefined

	/* Eng og'ir qarz bo'lagi — kartada YAGONA muhim raqam.
	   Ilgari bu yerda rangli yo'lakcha turardi, lekin u o'ng paneldagi
	   ro'yxat bilan AYNAN bir xil ma'lumotni takrorlar, kartani boshqasidan
	   baland qilar va (69% eski bo'lgani uchun) «butun qizil chiziq» bo'lib
	   ko'rinardi. */
	const worst = useMemo(() => {
		const rows = debt.aging || []
		const sum = rows.reduce((s, a) => s + Number(a.amount || 0), 0)
		if (sum <= 0) return null
		const w = rows.reduce((a, b) => (Number(b.amount || 0) > Number(a?.amount || 0) ? b : a), null)
		return w ? { ...w, pct: Math.round(Number(w.amount || 0) / sum * 100) } : null
	}, [debt.aging])

	/* ═══ GRAFIK ═══ */
	const series = useMemo(() => {
		const cur = chart[tab.id] || []
		const prev = chart['prev_' + tab.id] || []
		const run = arr => { let s = 0; return arr.map(v => (s += Number(v) || 0)) }
		/* Kunlik rejimda o'tgan davr KO'RSATILMAYDI: 31 kun × 2 seriya = 62
		   ustun, ko'pchiligi nol — o'qib bo'lmaydi. Taqqoslash yig'indi
		   rejimida qoladi (u shu ish uchun yaratilgan). */
		return cumulative
			? [
				{ name: 'Бу давр', data: run(cur) },
				{ name: 'Ўтган давр', data: run(prev) },
			]
			: [{ name: 'Бу давр', data: cur.map(v => Number(v) || 0) }]
	}, [chart, tab.id, cumulative])

	/* Y o'qi birligi BUTUN o'q uchun BIR MARTA tanlanadi — aks holda bitta
	   o'qda «900 000» va «1 mln» aralashib ketardi. */
	const yUnit = useMemo(() => {
		if (!tab.money) return { div: 1, suffix: '', dec: 1 }
		let max = 0
		series.forEach(s => s.data.forEach(v => { if (v > max) max = v }))
		if (max >= 1e9) return { div: 1e9, suffix: ' млрд', dec: 1 }
		if (max >= 1e6) return { div: 1e6, suffix: ' млн', dec: 0 }
		if (max >= 1e3) return { div: 1e3, suffix: ' минг', dec: 0 }
		return { div: 1, suffix: '', dec: 0 }
	}, [series, tab.money])

	const shortNum = useCallback(
		v => (Number(v) === 0 ? '0' : (Number(v) / yUnit.div).toFixed(yUnit.dec) + yUnit.suffix),
		[yUnit])

	const curTotal = (chart[tab.id] || []).reduce((s, v) => s + (Number(v) || 0), 0)
	const prevTotal = (chart['prev_' + tab.id] || []).reduce((s, v) => s + (Number(v) || 0), 0)
	const nDays = (chart.labels || []).length

	const chartOptions = {
		chart: {
			type: cumulative ? 'area' : 'bar', toolbar: { show: false },
			background: 'transparent', fontFamily: 'inherit', parentHeightOffset: 0,
			// 800 ms mount animatsiyasi har rejim almashganda qayta o'ynardi
			animations: { enabled: true, easing: 'easeinout', speed: 350, animateGradually: { enabled: false } },
		},
		colors: [tab.color, '#adb5bd'],
		// Punktir = o'tgan davr. Rang emas, SHAKL farqlaydi — chop etilganda ham ajraladi.
		stroke: cumulative
			? { curve: 'smooth', lineCap: 'round', width: [3, 2], dashArray: [0, 6] }
			: { show: false, width: 0, dashArray: 0 },
		fill: cumulative
			? {
				type: ['gradient', 'solid'],
				/* opacity[0] YOZILMAYDI: gradient.opacityFrom/To uni bekor qiladi
				   (Fill.js:327). opacity[1]=0 esa HAQIQIY ishlaydi — o'tgan
				   davrning maydoni chizilmaydi, faqat punktir chizig'i qoladi.
				   shade:'light' + shadeIntensity:0 — aks holda maydon pastda
				   qorayadi va qorong'i mavzuda loyqaga aylanadi. */
				opacity: [1, 0],
				gradient: { type: 'vertical', shade: 'light', shadeIntensity: 0, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 100] },
			}
			: { type: 'solid', opacity: [0.95] },
		plotOptions: { bar: { columnWidth: '60%', borderRadius: 3, borderRadiusApplication: 'end' } },
		// Bitta kunlik davrda («Bugun»/«Kecha») nuqta MAJBURIY — aks holda
		// chiziq chizilmaydi va grafik butunlay bo'sh ko'rinadi.
		markers: { size: nDays <= 2 ? 5 : 0, strokeWidth: 0, hover: { size: 5, sizeOffset: 0 } },
		dataLabels: {
			enabled: cumulative,
			/* Faqat joriy davrning OXIRGI nuqtasi. Ko'k chiziq kulrangdan past
			   qolsa ham qiymati o'qiladi. Boshqa nuqtalarda bo'sh matn — Apex
			   bo'sh matnga fon chizmaydi (DataLabels.js:341). */
			formatter: (val, o) => (
				o.seriesIndex === 0 && o.w.globals.series[0]
					&& o.dataPointIndex === o.w.globals.series[0].length - 1
					? shortNum(val) : ''),
			textAnchor: 'end', offsetX: -2, offsetY: -6,
			style: { fontSize: '12px', fontWeight: 700 },
			background: { enabled: true, borderWidth: 0, borderRadius: 3, padding: 5, opacity: 1 },
		},
		// Legenda o'chiq — uning o'rniga sarlavha qatorida o'z ixcham jamlanmamiz
		legend: { show: false },
		grid: {
			borderColor: '#eef0f4', strokeDashArray: 0,
			xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } },
			padding: { top: 0, right: 16, bottom: 0, left: 4 },
		},
		xaxis: {
			categories: chart.labels || [],
			tickPlacement: 'on',
			labels: {
				rotate: 0, rotateAlways: false, hideOverlappingLabels: false, trim: false,
				style: { fontSize: '11px' },
				/* Har n-chi kun + MAJBURAN oxirgi kun.
				   ⚠️ `tickAmount` ISHLATILMAYDI: u belgilarni teng qadamda
				      tanlaydi va 31/(6+1)=4 qadamda OXIRGI KUN tushib qoladi —
				      eganing eng kerakli kuni. */
				formatter: (val, _ts, o) => {
					const n = (chart.labels || []).length
					if (n <= 10) return val
					const step = Math.ceil(n / 6)
					const i = o && typeof o.i === 'number' ? o.i : 0
					return (i % step === 0 || i === n - 1) ? val : ''
				},
			},
			axisTicks: { show: false }, axisBorder: { show: false },
			tooltip: { enabled: false },
			crosshairs: { stroke: { color: '#c9d1de', width: 1, dashArray: 3 } },
		},
		yaxis: {
			min: 0,
			// `min` berilgani uchun forceNiceScale MAJBURIY (Scales.js:274) —
			// bo'lmasa o'q «0 / 261 mln / 522 mln» kabi xunuk qadam beradi
			forceNiceScale: true,
			tickAmount: 4,
			labels: { style: { fontSize: '11px' }, formatter: shortNum },
		},
		tooltip: {
			shared: true,
			y: { formatter: v => tab.money ? som(v) : Number(v).toFixed(3) + ' т' },
		},
	}

	const topColors = { profnastil: '#0d6efd', rulon: 'var(--an-violet, #6f42c1)', aksessuar: '#198754', tunukafon: '#dc3545', produkt: '#fd7e14' }
	const topLabels = { profnastil: 'Профнастил', rulon: 'Рулон', aksessuar: 'Аксессуар', tunukafon: 'Туникофон', produkt: 'Продукт' }

	/* 2-qator kartalari — soni davrga qarab 2..4 ga o'zgaradi: qaytarish
	   yoki chegirma bo'lmagan davrda o'sha kartalar umuman chizilmaydi
	   (nol turgan karta joy egallashdan boshqa hech narsa qilmaydi). */
	const kpi2 = [
		{
			label: 'Жами тонна', icon: 'uil-weight', color: '#fd7e14', bg: '#fff3e0',
			value: kpi.total_tonna == null ? '—' : `${Number(kpi.total_tonna).toFixed(3)} т`,
			delta: delta.total_tonna,
		},
		{
			label: 'Ялпи даромад', icon: 'uil-arrow-growth',
			color: (kpi.gross_income ?? 0) >= 0 ? 'var(--ok-mid, #198754)' : 'var(--warn-ink, #dc3545)',
			bg: (kpi.gross_income ?? 0) >= 0 ? 'var(--ok-mid-bg, #e8f5e9)' : 'var(--warn-ink-bg, #fce4e4)',
			value: som(kpi.gross_income), delta: delta.gross_income,
		},
		...(Number(kpi.total_returned) > 0 ? [{
			label: 'Қайтарилган', icon: 'uil-corner-up-left', color: 'var(--warn-ink, #dc3545)', bg: 'var(--warn-ink-bg, #fce4e4)',
			value: som(kpi.total_returned), invert: true,
			sub: `${formatMoney(kpi.returned_count, 0)} та`,
		}] : []),
		...(Number(kpi.total_discount) > 0 ? [{
			label: 'Чегирма', icon: 'uil-percentage', color: 'var(--dk-text-2, #6c757d)', bg: 'var(--an-chip-bg, #f1f3f7)',
			value: som(kpi.total_discount),
		}] : []),
	]
	/* Ustun kengligi karta soniga QARAB O'ZGARMAYDI — 2 ta karta qolganda
	   ularni yarim ekranga cho'zish aynan «ichi bo'sh, cho'zilgan karta»
	   holatini qaytaradi. Qator to'lmasa to'lmaydi, lekin karta o'lchami
	   1-qator bilan bir xil qoladi va ko'z uchun to'r buzilmaydi. */
	// Ikkala qator ham BIR XIL to'rda sinsin — aks holda 768–1199px da
	// yuqorida 2 ta, pastda 4 ta ustun bo'lib ritm buziladi
	const col1 = hasMargin ? 'col-12 col-sm-6 col-xl-3' : 'col-12 col-sm-6 col-xl-4'

	return (
		<div className="an-page">
			<div className="page-header d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">Analitika</h4>
			</div>

			{/* ── Filtr ── */}
			<div className="card mb-3">
				<div className="card-body py-2 px-3">
					<div className="d-flex align-items-center gap-2 flex-wrap">
						{QUICK_FILTERS.map(f => (
							<button key={f.id}
								className={`btn btn-sm ${activeFilter === f.id ? 'btn-primary' : 'btn-outline-secondary'}`}
								onClick={() => applyQuick(f.id)}>{f.label}</button>
						))}
						<div style={{ width: 1, height: 28, background: 'var(--an-grid, #dee2e6)', margin: '0 4px' }} />
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker className="form-control form-control-sm" dateFormat="dd.MM.yyyy"
								selected={startDate} onChange={d => { setStartDate(d); setActiveFilter('') }} />
							<i className="uil uil-calendar-alt" />
						</div>
						<div className="calendar-input" style={{ width: 140 }}>
							<DatePicker className="form-control form-control-sm" dateFormat="dd.MM.yyyy"
								selected={endDate} onChange={d => { setEndDate(d); setActiveFilter('') }} />
							<i className="uil uil-calendar-alt" />
						</div>
						<button className="btn btn-sm btn-success"
							onClick={() => { setActiveFilter(''); load(startDate, endDate) }}>
							{loading ? <span className="spinner-border spinner-border-sm" />
								: <><i className="uil uil-sync" /> Yangilash</>}
						</button>
						{data &&
							<span className="text-muted ms-auto" style={{ fontSize: 12 }}>
								Таққослаш: {formatDate(new Date(data.prev.start), 'dd.MM')}–{formatDate(new Date(data.prev.end), 'dd.MM')}
							</span>
						}
					</div>
				</div>
			</div>

			{/* ═══ 1-QATOR: 4 ta asosiy raqam ═══ */}
			<div className="row g-3 mb-3">
				<div className={col1}>
					<KpiCard
						label="Савдо" icon="uil-usd-circle" color="#0d6efd" bg="#e7f1ff"
						value={som(kpi.total_sales)} delta={delta.total_sales}
						/* Chek soni ALOHIDA KARTA emas, shu yerda: oyiga ~25 buyurtma —
						   5 tasi kamaysa savdo 20% tushadi, buni pul ko'rsatishidan
						   OLDIN son ko'rsatadi. «O'rtacha» o'rniga MEDIANA: bitta
						   68 mln lik chek o'rtachani 2.4 barobar shishirgan edi. */
						sub={<>{formatMoney(kpi.total_cheques, 0)} буюртма · медиана {som(kpi.median_cheque)}</>}
					/>
				</div>

				<div className={col1}>
					<KpiCard
						label="Йиғилган пул" icon="uil-money-insert" color="#20c997" bg="#e0faf2"
						value={som(kpi.collected)} delta={delta.collected}
						sub={<>ўтган давр: {som(data?.kpi_prev?.collected)}</>}
					/>
				</div>

				<div className={col1}>
					<KpiCard
						label="Мижозлар қарзи" icon="uil-bill" color="var(--warn-ink, #dc3545)" bg="var(--warn-ink-bg, #fce4e4)"
						value={som(debt.total)}
						/* «852 mln» tushunarsiz, «96 kunlik savdo» tushunarli */
						right={debt.dso_days != null
							? <span className="an-chip" style={{ background: 'var(--warn-ink-bg, #dc35451f)', color: 'var(--warn-ink, #dc3545)', fontSize: 11 }}>
								{debt.dso_days} кунлик савдо
							</span>
							: null}
						sub={worst && <>{worst.label}: <span className="fw-bold" style={{ color: 'var(--warn-ink, #dc3545)' }}>{som(worst.amount)}</span> ({worst.pct}%)</>}
					/>
				</div>

				{hasMargin &&
					<div className={col1}>
						<KpiCard
							/* ⚠️ Rang `--an-violet`: qattiq #6610f2 qorong'i sirtda 2.3:1
							   kontrast berardi va binafsha (dark.css:123 — foydalanuvchi
							   talabi bilan qorong'ida ishlatilmaydi). */
							label="Маржа / тонна" icon="uil-weight" color="var(--an-violet, #6f42c1)" bg="var(--an-violet-bg, #f3eeff)"
							value={<>${formatMoney(kpi.margin_usd_t, 0)}
								<span style={{ fontSize: 15, marginLeft: 8 }}>{kpi.margin_pct}%</span></>}
							delta={delta.margin_usd_t}
							/* Egasi «nega tushdi?» deb so'ramasin — ikkala tomoni ko'rinsin.
							   Qamrov: raqam faqat partiyaga bog'langan sotuvlarni oladi. */
							sub={<>сотув ${formatMoney(kpi.sell_usd_t, 0)} · харид ${formatMoney(kpi.buy_usd_t, 0)} · қамров {kpi.margin_coverage}%</>}
						/>
					</div>
				}
			</div>

			{/* ═══ 2-QATOR: ikkinchi darajali raqamlar ═══ */}
			<div className="row g-3 mb-3">
				{kpi2.map((c, i) => (
					<div className={col1} key={i}>
						<KpiCard {...c} big={false} />
					</div>
				))}
			</div>

			{/* ═══ Grafik + «Пул қаерда қотган» ═══ */}
			<div className="row g-3 mb-3">
				<div className="col-lg-8">
					<div className="card h-100">
						<div className="card-body">
							<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
								<div className="btn-group btn-group-sm">
									{CHART_TABS.map(x => (
										<button key={x.id} type="button"
											className={'btn ' + (chartTab === x.id ? 'btn-primary' : 'btn-outline-secondary')}
											onClick={() => setChartTab(x.id)}>{x.label}</button>
									))}
								</div>
								<div className="form-check form-switch mb-0">
									<input className="form-check-input" type="checkbox" id="cumSw"
										checked={cumulative} onChange={e => setCumulative(e.target.checked)} />
									<label className="form-check-label text-muted" htmlFor="cumSw" style={{ fontSize: 12 }}>
										Йиғинди
									</label>
								</div>
								{/* Legenda o'rniga — u ham legenda, ham jamlanma vazifasini bajaradi */}
								<div className="text-muted ms-auto" style={{ fontSize: 12 }}>
									<span style={{ color: tab.color, fontWeight: 700 }}>●</span> Бу давр <b>{shortNum(curTotal)}</b>
									{cumulative &&
										<>
											<span className="mx-2">·</span>
											<span style={{ color: '#adb5bd', fontWeight: 700 }}>┄</span> Ўтган давр
											{chart.prev_label ? ` (${chart.prev_label})` : ''} <b>{shortNum(prevTotal)}</b>
										</>
									}
								</div>
							</div>
							<ReactApexChart
								/* ⚠️ `key` TAYANCH, olib tashlamang: react-apexcharts options'ni
								   JSON.stringify bilan solishtiradi va FUNKSIYALARNI tashlab
								   ketadi — «Савдо → Тонна» da faqat formatterlar o'zgaradi,
								   ya'ni u o'zgarish yo'q deb hisoblab qayta chizmasdi. */
								key={[chartTab, cumulative, nDays].join('|')}
								options={chartOptions} series={series}
								type={cumulative ? 'area' : 'bar'} height={340} />
						</div>
					</div>
				</div>

				<div className="col-lg-4">
					<div className="card h-100">
						<div className="card-body">
							<h6 className="card-title mb-3">Пул қаерда қотган</h6>

							<div className="an-kpi-lbl mb-2">Қарз ёши</div>
							{(debt.aging || []).filter(a => a.amount > 0).map(a => {
								const idx = a.bucket - 1
								const max = Math.max(...(debt.aging || []).map(x => Number(x.amount) || 0), 1)
								return (
									<Link key={a.bucket} to={`/report/debt-monitoring?age=${a.category}`}
										className="d-block text-decoration-none mb-2">
										<div className="d-flex justify-content-between align-items-center" style={{ fontSize: 12 }}>
											<span style={{ color: 'var(--an-ink, #495057)' }}>{a.label}</span>
											<span>
												<span className="fw-bold" style={{ color: AGING_COLORS[idx] }}>{som(a.amount)}</span>
												<span className="text-muted ms-2">{a.clients} та</span>
											</span>
										</div>
										<div style={{ height: 5, background: 'var(--ca-track, #eef0f4)', borderRadius: 3, marginTop: 3 }}>
											<div style={{ width: `${Number(a.amount) / max * 100}%`, height: '100%', background: AGING_COLORS[idx], borderRadius: 3 }} />
										</div>
									</Link>
								)
							})}

							<hr className="my-3" />

							{/* Ilgari bu yerda faqat SON turardi («Тайёр 18»). 18 ta chek
							    aslida 178.7 mln so'm va o'rtacha 26 kun kutyapti. */}
							<Link to="/production-board" className="d-flex justify-content-between align-items-center p-2 rounded text-decoration-none mb-2"
								style={{ background: 'var(--ca-soft, #f8f9fa)' }}>
								<div className="d-flex align-items-center gap-2">
									<i className="uil-box" style={{ color: '#0d6efd', fontSize: 16 }} />
									<div>
										<div style={{ fontSize: 13, color: 'var(--an-ink, #495057)' }}>Тайёр (омборда)</div>
										{stuck.ready?.cnt > 0 &&
											<div className="text-muted" style={{ fontSize: 11 }}>
												ўртача {stuck.ready.avg_days} кун · энг эскиси {stuck.ready.max_days} кун
											</div>}
									</div>
								</div>
								<div className="text-end">
									<div className="fw-bold" style={{ color: '#0d6efd', fontSize: 14 }}>{som(stuck.ready?.amount)}</div>
									<div className="text-muted" style={{ fontSize: 11 }}>{stuck.ready?.cnt ?? 0} та</div>
								</div>
							</Link>

							<Link to="/production-board" className="d-flex justify-content-between align-items-center p-2 rounded text-decoration-none mb-2"
								style={{ background: 'var(--ca-soft, #f8f9fa)' }}>
								<div className="d-flex align-items-center gap-2">
									<i className="uil-cog" style={{ color: '#f59e0b', fontSize: 16 }} />
									<span style={{ fontSize: 13, color: 'var(--an-ink, #495057)' }}>Ишлаб чиқаришда</span>
								</div>
								<div className="text-end">
									<div className="fw-bold" style={{ color: '#f59e0b', fontSize: 14 }}>{som(stuck.in_production?.amount)}</div>
									<div className="text-muted" style={{ fontSize: 11 }}>{stuck.in_production?.cnt ?? 0} та</div>
								</div>
							</Link>

							<div className="d-flex justify-content-between align-items-center p-2 rounded"
								style={{ background: 'var(--ca-soft, #f8f9fa)' }}>
								<div className="d-flex align-items-center gap-2">
									<i className="uil-truck" style={{ color: 'var(--ok-mid, #198754)', fontSize: 16 }} />
									<span style={{ fontSize: 13, color: 'var(--an-ink, #495057)' }}>Сотилган (давр)</span>
								</div>
								<span className="fw-bold" style={{ color: 'var(--ok-mid, #198754)', fontSize: 16 }}>{stuck.shipped ?? 0}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ═══ TOP mahsulotlar — SAVDO ulushi (%) ═══ */}
			<div className="row g-3">
				{['profnastil', 'aksessuar', 'tunukafon', 'rulon', 'produkt'].map(key => {
					const group = top[key] || {}
					const items = group.data || []
					const color = topColors[key]
					return (
						<div className="col-md-6 col-xl-4" key={key}>
							<div className="card h-100">
								<div className="card-body">
									<div className="d-flex justify-content-between align-items-start mb-3">
										<div>
											<h6 className="mb-0" style={{ color }}>Топ {topLabels[key]}</h6>
											{/* ⚠️ Bu summa 7 QATOR yig'indisi, butun kategoriya savdosi
											    EMAS — foizlar aynan shundan olinadi va 100% ga yig'iladi.
											    Summani olib tashlasak «%» nimadan olingani noma'lum qoladi. */}
											<small className="text-muted">{items.length} та маҳсулот · савдо улуши</small>
										</div>
										<div className="fw-bold" style={{ color, fontSize: 14 }}>{som(group.total_sales)}</div>
									</div>
									{items.length === 0 ? (
										<div className="text-center text-muted py-4" style={{ fontSize: 13 }}>Ma'lumot yo'q</div>
									) : (
										<div className="d-flex flex-column gap-2">
											{items.map((item, i) => {
												/* Qaytarish sotuvdan ko'p bo'lsa qator MANFIY, backend esa
												   foizni abs() bilan beradi (topShare) — ilgari summa
												   ko'rinib turgani uchun sezilardi, endi ochiq aytamiz. */
												const neg = Number(item.total_sales) < 0
												const pct = Math.min(Number(item.percentage) || 0, 100)
												const rowColor = neg ? 'var(--warn-ink, #dc3545)' : color
												const unit = key === 'rulon'
													? `${Number(item.total_tonna || 0).toFixed(3)} т`
													: key === 'tunukafon'
														? `${Number(item.total_kvm || 0).toFixed(1)} м²`
														: Number(item.total_metr) > 0
															? `${som(item.total_metr)} м`
															: `${som(item.total_qty)} дона`
												return (
													/* Summa va o'lchov birligi QATORDAN olib tashlandi
													   (foydalanuvchi qarori) — ular sichqoncha tegsa
													   ko'rinadi. Yo'lakcha qator OSTIDA emas, ICHIDA:
													   keng ekranda qator o'rtasi bo'm-bo'sh qolardi. */
													<div key={i} className="d-flex align-items-center gap-2"
														title={`${item.name}\n${som(item.total_sales)} · ${unit}`}>
														<span style={{ fontWeight: 700, color: rowColor, fontSize: 12, minWidth: 22 }}>#{i + 1}</span>
														<span className="text-truncate" style={{ fontSize: 13, flex: '1 1 auto', minWidth: 60 }}>{item.name}</span>
														<div style={{ flex: '0 1 180px', height: 6, background: 'var(--ca-track, #e9ecef)', borderRadius: 3 }}>
															<div style={{ width: `${pct}%`, height: '100%', background: rowColor, borderRadius: 3, transition: 'width .4s' }} />
														</div>
														<span className="fw-bold" style={{ color: rowColor, fontSize: 13, minWidth: 48, textAlign: 'right' }}>
															{neg ? '-' : ''}{pct.toFixed(1)}%
														</span>
													</div>
												)
											})}
										</div>
									)}
								</div>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}
